import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { formatTimestamp } from '../apiUtils';

// CSS
import './ApiEndpoints.css';

// Constants
const MAX_VISIBLE_ENDPOINTS = 60;

/**
 * ApiEndpoints
 * 
 * This component renders a list of API endpoints based on the provided OpenAPI schema data. It displays the path, 
 * supported HTTP methods, and summaries for each endpoint. If no endpoints are found, it shows a message prompting 
 * the user to check their API configuration.
 * 
 * @param {array} visibleOpenapiPathRows - An array of objects representing the API paths and their details to be displayed.
 * @param {string} openapiGeneratedAt - A timestamp indicating when the OpenAPI schema was generated, used for display purposes.
 * 
 * @returns {JSX.Element} The rendered component displaying the API endpoints.
 */
const ApiEndpoints = ({
  visibleOpenapiPathRows,
  openapiGeneratedAt,
}) => {

  // ----- CONTEXT ----- //

  // Theme Context
  const { theme } = useTheme();

  // ----- RENDER ----- //

  return (

    <section className="api-endpoints-container">

      {/* SUBTITLE */}
      <div className="api-endpoints-subtitle"
        style={{
          color: theme.foregroundMuted
        }}>
        {visibleOpenapiPathRows.length} documented paths generated at {formatTimestamp(openapiGeneratedAt)}
      </div>

      {/* ENDPOINT ROWS */}
      <div className="api-endpoints-list">

        {visibleOpenapiPathRows.slice(0, MAX_VISIBLE_ENDPOINTS).map((row) => (

          <div
            key={row.path}
            className="api-endpoints-row"
            style={{ 
              borderColor: theme.backgroundHover 
            }}
          >
            {/* PATH */}
            <div className="api-endpoints-path">{row.path}</div>

            {/* METHODS */}
            <div className="api-endpoints-methods">
              {(row.methods || []).map((methodRow) => (
                <span
                  key={`${row.path}-${methodRow.method}`}
                  className="api-endpoints-chip"
                  style={{
                    borderColor: theme.backgroundHover,
                    color: theme.foregroundMuted
                  }}>
                  {methodRow.method}
                </span>
              ))}
            </div>

            {/* SUMMARIES */}
            {(row.methods || []).map((methodRow) => (
              <div
                key={`${row.path}-${methodRow.method}-summary`}
                className="api-endpoints-summary"
                style={{
                  color: theme.foregroundMuted
                }}>
                {methodRow.method}: {methodRow.summary || 'No summary'}
              </div>
            ))}
          </div>

        ))}

      </div>
      
    </section>
  );
};

export default ApiEndpoints;
